import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useFavorites = (userId?: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch user's favorites
  const { data: favorites, isLoading } = useQuery({
    queryKey: ["favorites", userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from("favorites")
        .select(`
          id,
          listing_id,
          created_at,
          listings (
            id,
            title,
            address,
            base_price_day,
            size,
            photos
          )
        `)
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
  });

  // Add favorite
  const addFavorite = useMutation({
    mutationFn: async (listingId: string) => {
      if (!userId) throw new Error("Must be logged in");
      const { error } = await supabase
        .from("favorites")
        .insert({ user_id: userId, listing_id: listingId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favorites", userId] });
      toast({
        title: "Tilføjet til favoritter",
        description: "Parkeringspladsen er gemt i dine favoritter",
      });
    },
    onError: () => {
      toast({
        title: "Fejl",
        description: "Kunne ikke tilføje til favoritter",
        variant: "destructive",
      });
    },
  });

  // Remove favorite
  const removeFavorite = useMutation({
    mutationFn: async (listingId: string) => {
      if (!userId) throw new Error("Must be logged in");
      const { error } = await supabase
        .from("favorites")
        .delete()
        .eq("user_id", userId)
        .eq("listing_id", listingId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["favorites", userId] });
      toast({
        title: "Fjernet fra favoritter",
        description: "Parkeringspladsen er fjernet fra dine favoritter",
      });
    },
    onError: () => {
      toast({
        title: "Fejl",
        description: "Kunne ikke fjerne fra favoritter",
        variant: "destructive",
      });
    },
  });

  // Check if listing is favorited
  const isFavorite = (listingId: string) => {
    return favorites?.some((f) => f.listing_id === listingId) || false;
  };

  return {
    favorites,
    isLoading,
    addFavorite: addFavorite.mutate,
    removeFavorite: removeFavorite.mutate,
    isFavorite,
  };
};
